import { BoardConfiguration } from '@shared/models/boardConfiguration';
import DeparturesModule from '@modules/departures/components/DeparturesModule';
import ArrivalsModule from '@modules/arrivals/components/ArrivalsModule';
import StopHeader from '@shared/components/StopHeader';
import TrafficSituationsModule from '@modules/traffic-situations/components/TrafficSituationsModule';
import styles from './Board.module.scss';
import classNames from 'classnames';
import { useCallback, useMemo, useRef, useState } from 'react';
import {
  DEPARTURE_PAGING_DEFAULT,
  DEPARTURE_SORTING_DEFAULT,
  DEPARTURE_TIME_FORMAT_DEFAULT,
  HORIZONTAL_BOARD_MINIMUM_ASPECT_RATIO,
  MILLISECONDS_OF_ERROR_BEFORE_SHOWING_ERROR_MESSAGE,
  SHOW_PLATFORMS_IN_HEADER_DEFAULT,
  VEHICLE_BOARD_DEPARTURE_PAGING_DEFAULT,
  VEHICLE_BOARD_DEPARTURE_TIME_FORMAT_DEFAULT,
  VEHICLE_BOARD_DEPARTURE_SORTING_DEFAULT,
  VEHICLE_BOARD_DEPARTURES_TIME_SPAN_DEFAULT,
  VEHICLE_BOARD_MAX_CONNECTION_PAGES_BEFORE_CHANGING,
  DEPARTURES_TIME_SPAN_DEFAULT,
} from '@shared/constants';
import useElementSize from '@shared/hooks/useElementSize';
import { TrafficSituation } from '@modules/traffic-situations/models/trafficSituation';
import VehicleModule from '@modules/vehicle/components/VehicleModule';
import { LayoutContext } from '@shared/contexts/LayoutContext';
import useIsTimePassed from '@shared/hooks/useIsTimePassed';
import { useAppSelector } from '@shared/store';
import { DeepReadonly } from 'ts-essentials';
import Placeholder from './Placeholder';
import ExtendedInformationModule from '@modules/extended-information/components/ExtendedInformationModule';
import { ExtendedMessage } from '@modules/extended-information/models/extendedMessage';
import { JourneyProgressStatus } from '@modules/vehicle/models/protobuf/Journey';
import useVehicle from '@modules/vehicle/hooks/useVehicle';

interface BoardProps {
  hasServerSentEventsConnectionError: boolean;
  boardConfiguration: BoardConfiguration;
}

type VehicleSecondaryContent = 'connections' | 'traffic-situations';

function isApproachingOrAtStop(
  journeyProgressStatus: JourneyProgressStatus | undefined,
): boolean {
  return (
    journeyProgressStatus ===
      JourneyProgressStatus.JOURNEY_PROGRESS_APPROACHING_STOP ||
    journeyProgressStatus ===
      JourneyProgressStatus.JOURNEY_PROGRESS_REACHED_STOP ||
    journeyProgressStatus ===
      JourneyProgressStatus.JOURNEY_PROGRESS_ARRIVED_STOP
  );
}

export default function Board({
  hasServerSentEventsConnectionError,
  boardConfiguration,
}: BoardProps) {
  const stopHeaderState = useAppSelector(
    (state) => state.stopHeaders[boardConfiguration.id],
  );
  const departureRowsState = useAppSelector(
    (state) => state.departureRows[boardConfiguration.id],
  );
  const arrivalRowsState = useAppSelector(
    (state) => state.arrivalRows[boardConfiguration.id],
  );
  const trafficSituations = useAppSelector(
    (state) => state.trafficSituations[boardConfiguration.id]?.resources,
  );
  const extendedMessages = useAppSelector(
    (state) => state.extendedMessages[boardConfiguration.id]?.resources,
  );
  const journeyProgressStatus = useAppSelector(
    (state) => state.vehicle.journeyProgressStatus,
  );
  const vehicle = useVehicle();
  const realTimeJourney = useAppSelector(
    (state) => state.journeys[boardConfiguration.id]?.resource,
  );
  const boardRef = useRef<HTMLDivElement>(null);
  const boardSize = useElementSize(boardRef);
  const layout =
    boardSize.width === undefined ||
    boardSize.height === undefined ||
    boardSize.width > boardSize.height * HORIZONTAL_BOARD_MINIMUM_ASPECT_RATIO
      ? 'horizontal'
      : 'vertical';
  const isUltraWide =
    boardSize.width !== undefined &&
    boardSize.height !== undefined &&
    boardSize.width / boardSize.height >= 3;

  const departureRowsError = useIsTimePassed(
    departureRowsState?.errorStartTime,
    MILLISECONDS_OF_ERROR_BEFORE_SHOWING_ERROR_MESSAGE,
  );
  const arrivalRowsError = useIsTimePassed(
    arrivalRowsState?.errorStartTime,
    MILLISECONDS_OF_ERROR_BEFORE_SHOWING_ERROR_MESSAGE,
  );

  const hasVehicleConnections =
    vehicle !== undefined &&
    boardConfiguration.type === 'vehicle' &&
    isUltraWide &&
    vehicle.journey !== undefined &&
    vehicle.journey !== 'off-route' &&
    !hasServerSentEventsConnectionError &&
    !departureRowsError &&
    vehicle.journey.currentCallSequenceNumber ===
      realTimeJourney?.connectionsSequenceNumber &&
    departureRowsState?.resources !== undefined &&
    departureRowsState.resources.length > 0;

  const hasVehicleTrafficSituations = useMemo(
    () =>
      trafficSituations?.some(
        (trafficSituation) => trafficSituation.context === 'vehicle',
      ) ?? false,
    [trafficSituations],
  );

  const [vehicleSecondaryContent, setVehicleSecondaryContent] =
    useState<VehicleSecondaryContent>('connections');
  const vehicleConnectionPagesViewedRef = useRef(0);

  const isShowingVehicleConnections =
    hasVehicleConnections &&
    (isApproachingOrAtStop(journeyProgressStatus) ||
      !hasVehicleTrafficSituations ||
      vehicleSecondaryContent === 'connections');

  const trafficSituationsContext =
    boardConfiguration.type === 'vehicle' && !isShowingVehicleConnections
      ? 'vehicle'
      : 'stop';

  // Traffic situations shown while connections are unavailable complete their cycle before connections take over
  if (
    trafficSituationsContext === 'vehicle' &&
    hasVehicleTrafficSituations &&
    vehicleSecondaryContent !== 'traffic-situations'
  ) {
    setVehicleSecondaryContent('traffic-situations');
  }

  const onVehicleConnectionsPageViewed = useCallback((isLastPage: boolean) => {
    vehicleConnectionPagesViewedRef.current += 1;
    if (
      isLastPage ||
      vehicleConnectionPagesViewedRef.current >=
        VEHICLE_BOARD_MAX_CONNECTION_PAGES_BEFORE_CHANGING
    ) {
      vehicleConnectionPagesViewedRef.current = 0;
      setVehicleSecondaryContent('traffic-situations');
    }
  }, []);

  const onVehicleTrafficSituationsCycleCompleted = useCallback(() => {
    vehicleConnectionPagesViewedRef.current = 0;
    setVehicleSecondaryContent('connections');
  }, []);

  const trafficSituationsForContext = useMemo(
    () =>
      trafficSituations?.filter(
        (trafficSituation) =>
          trafficSituation.context === trafficSituationsContext,
      ),
    [trafficSituations, trafficSituationsContext],
  );

  const renderHeader =
    ((boardConfiguration.type === 'stop-area' ||
      boardConfiguration.type === 'stop-points') &&
      (boardConfiguration.modules.includes('departures') ||
        boardConfiguration.modules.includes('arrivals') ||
        boardConfiguration.modules.includes('trafficSituations'))) ||
    isShowingVehicleConnections;
  const isStopConfiguration =
    boardConfiguration.type === 'stop-area' ||
    boardConfiguration.type === 'stop-points';
  const renderDeparturesModule =
    (isStopConfiguration &&
      boardConfiguration.modules.includes('departures')) ||
    isShowingVehicleConnections;
  const renderArrivalsModule =
    isStopConfiguration && boardConfiguration.modules.includes('arrivals');
  const renderVehicleModule = boardConfiguration.type === 'vehicle';
  const renderPlaceholder =
    renderVehicleModule || hasExtendedInformationModule(boardConfiguration);
  const showPlatformsInHeader =
    boardConfiguration.type === 'stop-points' &&
    (boardConfiguration.showPlatformsInHeader ??
      SHOW_PLATFORMS_IN_HEADER_DEFAULT);

  function shouldRenderTrafficSituationsModule(
    trafficSituations: DeepReadonly<TrafficSituation[]> | undefined,
  ): trafficSituations is DeepReadonly<TrafficSituation[]> {
    return (
      (boardConfiguration.type === 'vehicle' ||
        ((boardConfiguration.type === 'stop-area' ||
          boardConfiguration.type === 'stop-points') &&
          boardConfiguration.modules.includes('trafficSituations'))) &&
      trafficSituations !== undefined &&
      trafficSituations.length > 0
    );
  }

  function hasExtendedInformationModule(
    boardConfiguration: BoardConfiguration,
  ): boolean {
    return boardConfiguration.type === 'information';
  }

  function shouldRenderExtendedInformationModule(
    extendedMessages: DeepReadonly<ExtendedMessage[]> | undefined,
  ): extendedMessages is DeepReadonly<ExtendedMessage[]> {
    return (
      hasExtendedInformationModule(boardConfiguration) &&
      extendedMessages !== undefined &&
      extendedMessages.length > 0
    );
  }

  const departureSorting = isStopConfiguration
    ? (boardConfiguration.departureSorting ?? DEPARTURE_SORTING_DEFAULT)
    : VEHICLE_BOARD_DEPARTURE_SORTING_DEFAULT;
  const departurePaging = isStopConfiguration
    ? (boardConfiguration.departurePaging ?? DEPARTURE_PAGING_DEFAULT)
    : VEHICLE_BOARD_DEPARTURE_PAGING_DEFAULT;
  const departureTimeFormat = isStopConfiguration
    ? (boardConfiguration.departureTimeFormat ?? DEPARTURE_TIME_FORMAT_DEFAULT)
    : VEHICLE_BOARD_DEPARTURE_TIME_FORMAT_DEFAULT;
  const departureTimeSpan = isStopConfiguration
    ? (boardConfiguration.departureTimeSpan ?? DEPARTURES_TIME_SPAN_DEFAULT)
    : VEHICLE_BOARD_DEPARTURES_TIME_SPAN_DEFAULT;
  const departureShowPlatform = isStopConfiguration
    ? boardConfiguration.type === 'stop-area' ||
      boardConfiguration.stopPointGids.length > 1
    : true;

  return (
    <LayoutContext.Provider value={layout}>
      <div
        ref={boardRef}
        className={classNames(
          styles.board,
          styles[`board--${layout}`],
          isUltraWide && styles['board--ultra-wide'],
          renderHeader && styles['board--has-header'],
          (renderDeparturesModule || renderArrivalsModule) &&
            styles['board--has-departures-or-arrivals'],
          shouldRenderTrafficSituationsModule(trafficSituationsForContext) &&
            styles['board--has-traffic-situations'],
          renderVehicleModule && styles['board--has-vehicle'],
          shouldRenderExtendedInformationModule(extendedMessages) &&
            styles['board--has-extended-information'],
        )}
      >
        {renderHeader && (
          <div className={styles.header}>
            <StopHeader
              header={stopHeaderState?.resource}
              showPlatforms={showPlatformsInHeader}
              showingArrivals={renderArrivalsModule}
              departureRows={departureRowsState?.resources}
            />
          </div>
        )}

        {renderDeparturesModule && (
          <div className={styles['departures-or-arrivals']}>
            <DeparturesModule
              departureSorting={departureSorting}
              departurePaging={departurePaging}
              departureTimeFormat={departureTimeFormat}
              departureTimeSpan={departureTimeSpan}
              showPlatform={departureShowPlatform}
              departureRows={departureRowsState?.resources}
              departureRowsError={
                hasServerSentEventsConnectionError || departureRowsError
              }
              onPageViewed={
                boardConfiguration.type === 'vehicle'
                  ? onVehicleConnectionsPageViewed
                  : undefined
              }
            />
          </div>
        )}

        {renderArrivalsModule && (
          <div className={styles['departures-or-arrivals']}>
            <ArrivalsModule
              boardConfiguration={boardConfiguration}
              arrivals={arrivalRowsState?.resources}
              arrivalRowsError={
                hasServerSentEventsConnectionError || arrivalRowsError
              }
            />
          </div>
        )}

        {renderVehicleModule && (
          <div className={styles.vehicle}>
            <VehicleModule vehicle={vehicle} />
          </div>
        )}

        {shouldRenderTrafficSituationsModule(trafficSituationsForContext) && (
          <div className={styles['traffic-situations']}>
            <TrafficSituationsModule
              key={trafficSituationsContext}
              trafficSituations={trafficSituationsForContext}
              context={trafficSituationsContext}
              onCycleCompleted={
                trafficSituationsContext === 'vehicle'
                  ? onVehicleTrafficSituationsCycleCompleted
                  : undefined
              }
            />
          </div>
        )}

        {shouldRenderExtendedInformationModule(extendedMessages) && (
          <div className={styles['extended-information']}>
            <ExtendedInformationModule extendedMessages={extendedMessages} />
          </div>
        )}

        {renderPlaceholder && (
          <div className={styles.placeholder}>
            <Placeholder />
          </div>
        )}
      </div>
    </LayoutContext.Provider>
  );
}
